import { resolveUnavailableActions } from "#shared/cycleActions.ts";
import type { ReviewCycleDetail } from "#shared/reviewCycleSchemas.ts";
import { countUnsubmittedReviews } from "#shared/unsubmittedReviews.ts";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { resolveLanguageKey } from "@packages/core/language/language.ts";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { PrintProvider } from "@packages/core-client/print/PrintProvider.tsx";
import { retryUnlessNotFound } from "@packages/core-client/query/retryUnlessNotFound.ts";
import { useLoadingIndicator } from "@packages/core-client/hooks/useLoadingIndicator.tsx";
import { RoutedBreadcrumb } from "@packages/core-client/routing/RoutedBreadcrumb.tsx";
import { useCurrentLanguage, useI18nextInstance } from "@squide/i18next";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useReviewCyclesClient } from "../../../api/reviewCyclesQuery.ts";
import { reviewCyclesI18NextInstanceKey } from "../../../i18n/i18next.ts";
import { CycleActionsMenu } from "../CycleActionsMenu.tsx";
import { CycleDetailHeader } from "./CycleDetailHeader.tsx";
import { CycleFilterBar } from "./CycleFilterBar.tsx";
import { CycleNudgeButton } from "./CycleNudgeButton.tsx";

const PLACEHOLDER_CYCLE: ReviewCycleDetail = {
    id: "skeleton-cycle",
    name: "Xxxxxxx xxxxxxxxxxx xxxxxx",
    state: "active",
    keepsAdmitting: false,
    isAcknowledgmentRequired: false,
    reviewTypes: ["self", "manager"],
    evaluationStartDate: "2026-01-01",
    evaluationEndDate: "2026-06-30",
    launchDate: "2026-01-05",
    creator: { id: "skeleton-creator", displayName: "Xxxxxxx Xxxxxxxx" },
    sections: [],
    unavailableActions: resolveUnavailableActions("active")
};

export interface CycleDetailHeadingProps {
    cycleId: string;
    ancestorCrumbs?: { label: string; href: string }[];
    canAdministerCycles?: boolean;
    getEditCycleHref?: (cycleId: string) => string;
    isEditInApp?: boolean;
    onCycleDeleted?: () => void;
    onCycleNotViewable?: () => void;
}

export function CycleDetailHeading({ cycleId, ancestorCrumbs, canAdministerCycles, getEditCycleHref, isEditInApp, onCycleDeleted, onCycleNotViewable }: CycleDetailHeadingProps) {
    const i18nextInstance = useI18nextInstance(reviewCyclesI18NextInstanceKey);
    const { t } = useTranslation("ReviewCycleDetailView", { i18n: i18nextInstance });
    const { queryUtils } = useReviewCyclesClient();
    const session = useSession();
    const language = resolveLanguageKey(useCurrentLanguage());

    const cycleOptions = queryUtils.getReviewCycle.queryOptions({ input: { orgId: session?.organizationId ?? "", cycleId } });

    const { data, isPending } = useQuery(
        createQueryOptions(cycleOptions, {
            queryKey: [...cycleOptions.queryKey, { language }],
            throwOnError: false,
            retry: retryUnlessNotFound
        })
    );

    const hasActionsMenu = !!canAdministerCycles && !!getEditCycleHref;

    const { data: cycleWideProgress } = useQuery(
        createQueryOptions(queryUtils.getCycleProgress.queryOptions({ input: { orgId: session?.organizationId ?? "", cycleId } }), {
            throwOnError: false,
            retry: retryUnlessNotFound,
            enabled: hasActionsMenu
        })
    );

    const isNotViewable = data?.state === "draft";

    useEffect(() => {
        if (isNotViewable) {
            onCycleNotViewable?.();
        }
    }, [isNotViewable, onCycleNotViewable]);

    const isLoading = useLoadingIndicator(isPending);
    const hasContent = (isLoading || Boolean(data)) && !isNotViewable;
    const cycle = isLoading ? PLACEHOLDER_CYCLE : (data ?? PLACEHOLDER_CYCLE);

    return (
        <PrintProvider>
            <div className="flex w-full flex-col gap-6">
                <output className="sr-only">{isLoading && t("liveRegion.loading")}</output>

                {hasContent && (
                    <Skeleton isLoading={isLoading} inert={isLoading}>
                        <div className="flex flex-col gap-6">
                            {ancestorCrumbs?.length ? <RoutedBreadcrumb ariaLabel={t("breadcrumbAriaLabel")} items={[...ancestorCrumbs, { label: cycle.name }]} /> : null}

                            <CycleDetailHeader
                                cycle={cycle}
                                actions={
                                    data ? (
                                        <div className="flex items-center gap-2">
                                            <CycleNudgeButton cycle={data} />
                                            {canAdministerCycles && getEditCycleHref && (
                                                <CycleActionsMenu
                                                    cycleId={data.id}
                                                    cycleName={data.name}
                                                    state={data.state}
                                                    unavailableActions={data.unavailableActions}
                                                    unsubmittedReviewCount={countUnsubmittedReviews(cycleWideProgress?.progress)}
                                                    getEditHref={getEditCycleHref}
                                                    isEditInApp={isEditInApp}
                                                    onDeleted={onCycleDeleted}
                                                    triggerSize="sq-md"
                                                />
                                            )}
                                        </div>
                                    ) : undefined
                                }
                            />
                        </div>
                    </Skeleton>
                )}

                <CycleFilterBar />
            </div>
        </PrintProvider>
    );
}
